import React, { ReactNode, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectOptions } from 'src/store/optionSlice';
import {
  INTERVIEW_TOPIC,
  ONBOARDING_STEP_LABEL,
  UserEntity,
} from 'src/store/userApi';
import { Alert } from 'src/ui/Alert';
import { Card } from 'src/ui/Card';
import {
  buildOptionsMap,
  decodeFieldValue,
  groupProfileKeys,
  OptionLike,
} from './profileFieldUtils';

const INTERVIEW_TOPIC_LABELS = {
  [INTERVIEW_TOPIC.INIT]: 'Initial',
  [INTERVIEW_TOPIC.LIFE_STYLE]: 'Life Style',
  [INTERVIEW_TOPIC.FAMILY]: 'Family',
  [INTERVIEW_TOPIC.RELATIONSHIP]: 'Relationship',
};

interface UserProfileReadOnlyProps {
  user: UserEntity;
  /** Profile field keys to omit from the grouped cards (e.g. when a field is
   * rendered in a dedicated section elsewhere). */
  excludeKeys?: string[];
}

const ReadOnlyRow = ({ label, value }: { label: string; value: string }) => {
  const isEmpty = value === '' || value === null || value === undefined;
  return (
    <div>
      <div
        style={{
          fontSize: 'var(--fs-xs)',
          color: 'var(--text-secondary)',
          fontWeight: 500,
          marginBottom: 2,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 'var(--fs-sm)',
          color: isEmpty ? 'var(--text-faint)' : 'var(--text)',
          wordBreak: 'break-word',
          whiteSpace: 'pre-wrap',
        }}
      >
        {isEmpty ? '—' : value}
      </div>
    </div>
  );
};

const SectionCard = ({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) => (
  <Card pad>
    <div
      style={{
        fontWeight: 600,
        fontSize: 'var(--fs-sm)',
        color: 'var(--text)',
        marginBottom: 12,
      }}
    >
      {title}
    </div>
    {children}
  </Card>
);

const ReadOnlyGroup = ({
  title,
  entries,
}: {
  title: string;
  entries: [string, string][];
}) => {
  if (entries.length === 0) return null;
  return (
    <SectionCard title={title}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: 12,
        }}
      >
        {entries.map(([key, value]) => (
          <ReadOnlyRow key={key} label={key} value={value} />
        ))}
      </div>
    </SectionCard>
  );
};

const UserProfileReadOnly = ({
  user,
  excludeKeys = [],
}: UserProfileReadOnlyProps) => {
  const [optionsMap, setOptionsMap] = useState<Record<string, OptionLike>>({});
  const allOptions = useSelector(selectOptions);

  useEffect(() => {
    if (Object.keys(allOptions).length) {
      setOptionsMap(buildOptionsMap(allOptions));
    }
  }, [allOptions]);

  const currentTopic = user.interviewProgress?.currentTopic;
  const completedTopics = (user.interviewProgress?.completedTopics || [])
    .map((topic) => INTERVIEW_TOPIC_LABELS[topic] ?? String(topic))
    .join(', ');

  const userInfoCard = (
    <SectionCard title="User Information">
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: 12,
        }}
      >
        <ReadOnlyRow label="User ID" value={user.id} />
        <ReadOnlyRow label="Email" value={user.email} />
        <ReadOnlyRow
          label="Onboarding Step"
          value={ONBOARDING_STEP_LABEL[user.onboardingStep]}
        />
        <ReadOnlyRow
          label="Interview Stage"
          value={String(user.interviewStage)}
        />
        <ReadOnlyRow
          label="Current Topic"
          value={currentTopic ? INTERVIEW_TOPIC_LABELS[currentTopic] : 'None'}
        />
        <ReadOnlyRow
          label="Completed Topics"
          value={completedTopics || 'None'}
        />
        <ReadOnlyRow
          label="Created"
          value={
            user.createdAt ? new Date(user.createdAt).toLocaleString() : ''
          }
        />
        <ReadOnlyRow
          label="Last Active"
          value={
            user.lastRequestAt
              ? new Date(user.lastRequestAt).toLocaleString()
              : ''
          }
        />
      </div>
    </SectionCard>
  );

  if (!user.profile) {
    return (
      <div className="col" style={{ gap: 16 }}>
        {userInfoCard}
        <Alert color="blue">No profile data available</Alert>
      </div>
    );
  }

  const profile = user.profile;
  const decode = (key: string, value: any): [string, string] => [
    key,
    decodeFieldValue(key, value, optionsMap),
  ];

  const groupCards = groupProfileKeys(profile)
    .map((group) =>
      ReadOnlyGroup({
        title: group.title,
        entries: group.keys
          .filter((key) => !excludeKeys.includes(key))
          .map((key) => decode(key, profile[key]))
          .filter(([, value]) => value !== ''),
      })
    )
    .filter(Boolean);

  const answerKeys = profile.answers ? Object.keys(profile.answers).sort() : [];
  const answerCard = ReadOnlyGroup({
    title: 'Interview Answers',
    entries: answerKeys
      .map((key) => decode(key, profile.answers?.[key]))
      .filter(([, value]) => value !== ''),
  });

  return (
    <div className="col" style={{ gap: 16 }}>
      {userInfoCard}
      {groupCards}
      {answerCard}
    </div>
  );
};

export default UserProfileReadOnly;
